import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import CircularProgress from "@mui/material/CircularProgress"
import { styled } from "@mui/material/styles"
import Tooltip from "@mui/material/Tooltip"
import Typography from "@mui/material/Typography"
import fileIcon from "assets/images/drag_drop.svg"
import axios from "axios"
import { setAlertFunc } from "components/AlertToaster/AlertSlice"
import { enqueueSnackbar } from "notistack"
import {
    DocHubUrlInfo,
    SubmitFileInfo,
    useEditDocumentsMutation,
    useUploadDocumentsMutation,
} from "pages/Common/fileUploadService"
import React, { useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { TStore } from "store/Store"

type Props = {
    /** Enables drag-and-drop file uploads and displays a drag-and-drop area in the UI.
     * @default false
     */
    allowDrop?: boolean
    /** Maximum number of files that can be uploaded
     * @default 1
     */
    maxFiles?: number
    /** Acceptable file types
     * @example ".xlsx, .xls, application/pdf"
     */
    accept?: string
    /** Maximum size of the file in MB
     * @default 50
     */
    maxSize?: number
    /** Callback function that is called when the files are changed
     * @param files - The files that are uploaded
     * @returns void
     */
    onChange?: (files: FileList) => void
    /**
     * The files the user just picked, fired in BOTH upload modes.
     *
     * `onChange` above only fires when the component is NOT in DocHub v3 mode, so a caller
     * passing `associatedId` + `fileType` never hears about a selection. This is the hook
     * for anything needing the raw File objects regardless - a local thumbnail, say, since
     * the upload response carries a temp S3 key rather than anything a browser can render.
     *
     * Additive: existing callers are unaffected.
     */
    onFilesPicked?: (files: FileList) => void
    /** Ticket ID for DocHub v3 upload. When provided with fileType, uses v3 API */
    associatedId?: number
    /** Document type for DocHub v3 upload (e.g., 'final_bill', 'kyc_document') */
    fileType?: string
    /** Callback when files are uploaded via DocHub v3. Returns SubmitFileInfo[] for use with submit_final_uploads.
     * previousFileInfos contains the file infos from the prior upload by this same uploader, so the parent can remove them. */
    onUpload?: (fileInfos: SubmitFileInfo[], previousFileInfos?: SubmitFileInfo[]) => void
    /** Disables the file uploader */
    disabled?: boolean
    /** Initial file count to display (for showing existing files) */
    initialFileCount?: number
    /** Names of existing files to display in tooltip */
    existingFileNames?: string[]
    /** Show error state with red border */
    error?: boolean
    is_site?: boolean
    is_sub_ticket?: boolean
    /** Sent with the upload so the document is saved against its project (optional). */
    projectId?: number
    /** Existing doc_ids of files already uploaded for this fileType. When provided
     *  (length > 0), the uploader calls editDocuments with these as `removed_ids`
     *  instead of uploadDocuments, replacing the existing docs. */
    docIds?: number[]
    /** Called with true when upload starts, false when it finishes (success or error) */
    onUploading?: (uploading: boolean) => void
}

const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
})

const FileUploaderV2 = ({
    maxFiles = 1,
    allowDrop = false,
    accept = "",
    maxSize = 50,
    onChange,
    onFilesPicked,
    associatedId,
    fileType,
    onUpload,
    disabled = false,
    initialFileCount = 0,
    existingFileNames = [],
    error = false,
    is_site = false,
    is_sub_ticket = false,
    projectId,
    docIds,
    onUploading,
}: Props) => {
    const dispatch = useDispatch()
    const userId = useSelector((state: TStore) => state.verifyLogin?.loginData?.user_data?.id)
    const [uploadDocuments] = useUploadDocumentsMutation()
    const [editDocuments] = useEditDocumentsMutation()
    const [dragActive, setDragActive] = useState(false)
    const [uploadedFilesCount, setUploadedFilesCount] = useState(initialFileCount)
    const [uploadedFileNames, setUploadedFileNames] = useState<string[]>(existingFileNames)
    const [isUploading, setIsUploading] = useState(false)
    const previousFileInfosRef = useRef<SubmitFileInfo[]>([])

    const maximumSize = maxSize * 1024 * 1024
    const acceptValueProvided = accept !== ""
    const useDocHubV3 = Boolean(associatedId && fileType)

    const validateFiles = (files: FileList | null): FileList | null => {
        if (!files || files.length === 0) return null

        // Check max files and return null if not valid
        if (files.length > maxFiles) {
            enqueueSnackbar(`You can upload upto ${maxFiles} files only`, { variant: "error" })
            console.warn(`Maximum ${maxFiles} file(s) can be selected`)
            return null
        }
        const validFiles: File[] = []
        for (let i = 0; i < files.length; i++) {
            const file = files[i]

            // Check file size
            if (file.size > maximumSize) {
                console.warn(`File ${file.name} exceeds maximum size of ${maxSize}MB`)
                continue
            }

            // Check file type if accept is specified
            if (acceptValueProvided) {
                const acceptTypes = accept.split(",").map(type => type.trim())
                const fileExtension = "." + file.name.split(".").pop()?.toLowerCase()
                const fileType = file.type.toLowerCase()
                const isAccepted = acceptTypes.some(acceptType => {
                    if (acceptType.startsWith(".")) {
                        return fileExtension === acceptType.toLowerCase()
                    }
                    if (acceptType.includes("/*")) {
                        const baseType = acceptType.split("/")[0]
                        return fileType.startsWith(baseType + "/")
                    }
                    return fileType === acceptType.toLowerCase()
                })

                if (!isAccepted) {
                    enqueueSnackbar(`File ${file.name} is not an accepted format`, { variant: "error" })
                    continue
                }
            }

            validFiles.push(file)
        }

        if (validFiles.length === 0) return null

        const dataTransfer = new DataTransfer()
        validFiles.forEach(file => dataTransfer.items.add(file))
        return dataTransfer.files
    }

    const handleFiles = async (files: FileList) => {
        const validFiles = validateFiles(files)
        if (!validFiles) return

        setUploadedFilesCount(validFiles.length)
        setUploadedFileNames(Array.from(validFiles).map(f => f.name))

        // Before the branch on purpose - this one is not conditional on upload mode.
        onFilesPicked?.(validFiles)

        if (useDocHubV3) {
            await uploadToDocHubV3(Array.from(validFiles))
        } else if (onChange) {
            onChange(validFiles)
        }
    }

    const uploadToDocHubV3 = async (fileArr: File[]) => {
        if (!associatedId || !fileType) return
        setIsUploading(true)
        onUploading?.(true)
        try {
            const fileEntry = { file_type: fileType, file_name: fileArr.map(f => f.name) }
            const isEdit = Array.isArray(docIds) && docIds.length > 0
            const response = isEdit
                ? await editDocuments({
                      ticket_id: associatedId,
                      user_id: userId,
                      removed_ids: docIds,
                      files: [fileEntry],
                      is_site: is_site,
                      is_sub_ticket: is_sub_ticket,
                      ...(projectId && { project_id: projectId }),
                  }).unwrap()
                : await uploadDocuments({
                      ticket_id: associatedId,
                      user_id: userId,
                      files: [fileEntry],
                      is_site: is_site,
                      is_sub_ticket: is_sub_ticket,
                      ...(projectId && { project_id: projectId }),
                  }).unwrap()

            const urls = response.urls[0]?.urls || []
            if (urls.length > 0) {
                await Promise.all(
                    urls.map(async (urlInfo: DocHubUrlInfo, index: number) => {
                        await axios.put(urlInfo.presigned_url, fileArr[index], {
                            headers: { "Content-Type": fileArr[index]?.type },
                        })
                    })
                )
                const fileInfos: SubmitFileInfo[] = urls.map((urlInfo: DocHubUrlInfo) => ({
                    file_url: urlInfo.file_url,
                    doc_id: urlInfo.doc_id,
                    report_id: urlInfo.report_id,
                }))
                const prev = previousFileInfosRef.current.length > 0 ? previousFileInfosRef.current : undefined
                previousFileInfosRef.current = fileInfos
                onUpload?.(fileInfos, prev)
            } else {
                console.error("No URLs returned from the server")
                dispatch(setAlertFunc({ type: "error", msg: "No URLs returned from server" }))
            }
        } catch (error) {
            console.error("Error uploading files:", error)
            dispatch(setAlertFunc({ type: "error", msg: "Error uploading files" }))
        } finally {
            setIsUploading(false)
            onUploading?.(false)
        }
    }

    const handleDrag = (event: React.DragEvent<HTMLElement>) => {
        event.preventDefault()
        event.stopPropagation()

        if (event.type === "dragenter" || event.type === "dragover") {
            setDragActive(true)
        } else if (event.type === "dragleave") {
            setDragActive(false)
        }
    }

    const handleDrop = (event: React.DragEvent<HTMLElement>) => {
        event.preventDefault()
        event.stopPropagation()
        setDragActive(false)

        if (event.dataTransfer?.files) {
            handleFiles(event.dataTransfer.files)
        }
    }

    const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            handleFiles(event.target.files)
        }
    }

    if (allowDrop) {
        return (
            <Box display={"flex"} flexDirection={"column"} width={"100%"}>
                <Box
                    height={"130px"}
                    border={dragActive ? "1px dashed #1976d2" : "1px dashed #B7BAC6"}
                    borderRadius={"8px"}
                    display={"flex"}
                    alignItems={"center"}
                    justifyContent={"center"}
                    flexDirection={"column"}
                    gap={2}
                    sx={{
                        backgroundColor: dragActive ? "rgba(25, 118, 210, 0.04)" : "transparent",
                        transition: "all 0.2s ease-in-out",
                        cursor: "pointer",
                    }}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}>
                    {isUploading ? <CircularProgress size={24} /> : <img src={fileIcon} alt="file" />}
                    <Box>
                        <Typography variant="label" fontSize={"11px"} fontWeight={"400"} textTransform={"none"}>
                            Drag and Drop file here or
                        </Typography>
                        <Button
                            component="label"
                            role={"button"}
                            variant="link"
                            tabIndex={-1}
                            disabled={disabled}
                            sx={{ fontSize: "11px" }}>
                            upload file
                            <VisuallyHiddenInput
                                type="file"
                                onChange={handleFileInputChange}
                                accept={accept}
                                multiple={maxFiles > 1}
                                disabled={disabled}
                            />
                        </Button>
                    </Box>
                </Box>
                <Box display={"flex"} justifyContent={acceptValueProvided ? "space-between" : "flex-end"} gap={1}>
                    {acceptValueProvided && (
                        <Typography
                            variant="label"
                            fontSize={"9px"}
                            fontWeight={"400"}
                            letterSpacing={"0.5%"}
                            color={"#70758D"}
                            textTransform={"none"}>
                            Supported formats: {accept}
                        </Typography>
                    )}
                    {maxSize && (
                        <Typography
                            variant="label"
                            fontSize={"9px"}
                            fontWeight={"400"}
                            letterSpacing={"0.5%"}
                            color={"#70758D"}
                            textTransform={"none"}>
                            Maximum size: {maxSize}MB
                        </Typography>
                    )}
                </Box>
            </Box>
        )
    }
    const buttonLabel =
        uploadedFilesCount > 0
            ? `${uploadedFilesCount} File${uploadedFilesCount > 1 ? "s" : ""} uploaded`
            : "Upload files"

    return (
        <Tooltip title={uploadedFileNames.length > 0 ? uploadedFileNames.join(", ") : ""} arrow>
            <Button
                fullWidth
                component="label"
                role={"button"}
                variant="outlined"
                tabIndex={-1}
                disabled={disabled}
                sx={{
                    height: "44px",
                    fontSize: "14px",
                    ...(uploadedFilesCount > 0 && { color: "statiqOrange.main" }),
                    ...(error && {
                        borderColor: "#d32f2f",
                        "&:hover": { borderColor: "#d32f2f" },
                    }),
                }}>
                {isUploading ? <CircularProgress size={20} /> : buttonLabel}
                <VisuallyHiddenInput
                    type="file"
                    onChange={handleFileInputChange}
                    multiple={maxFiles > 1}
                    accept={accept}
                    disabled={disabled}
                />
            </Button>
        </Tooltip>
    )
}

export default FileUploaderV2
